import PropTypes from "prop-types";
import {
    useState,
    useEffect,
    useRef,
    Fragment,
    useCallback,
} from "react";
import { Text } from "./../Text/Text";
import { Icon } from "./../Icon/Icon";
import { Tag } from "./../Tag/Tag";
import AddLogicTestResultModal from "../../../dialog/add_logics_test_results_modal";
import { TestResultQuestionImage } from "../QuestionImage/TestResultQuestionImage";

const QUESTION_TYPE_LABELS = {
    singlechoice: { header: "Single choice", tag: "Single-choice" },
    multiplechoice: { header: "Multiple choice", tag: "Multi-choice" },
    free: { header: "free text", tag: "Free text" },
    rankingscale: { header: "Ranking Scale", tag: "Ranking scale" },
    ratingscale: { header: "Rating Scale", tag: "Rating scale" },
    likertscale: { header: "Likert Matrix", tag: "Likert matrix" },
    likertscale_single: { header: "Likert Scale", tag: "Likert scale" },
};

function questionTypeHeaderLabel(type) {
    if (!type) return "";
    return QUESTION_TYPE_LABELS[type]?.header ?? type;
}

function questionTypeTagLabel(type) {
    if (!type) return "";
    return QUESTION_TYPE_LABELS[type]?.tag ?? type;
}

function useClickOutside(ref, onOutside) {
    useEffect(() => {
        function handleClickOutside(event) {
            if (ref.current && !ref.current.contains(event.target)) {
                onOutside();
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref, onOutside]);
}

function questionRowKey(item, index) {
    return item.id != null ? String(item.id) : `q-${index}`;
}

export function QuestionsDropdown({
    questionArray = [],
    changeActiveQuestionProp = () => { },
    dropdownOpen = () => { },
    resultCount,
    test,
    isActiveDropdown,
    questionType,
    active,
    isLogic,
    onHeaderHeightChange,
}) {
    const wrapperRef = useRef(null);
    const headerRef = useRef(null);
    const onHeaderHeightChangeRef = useRef(onHeaderHeightChange);
    onHeaderHeightChangeRef.current = onHeaderHeightChange;

    const [isListOpen, setListOpen] = useState(false);
    const [isActive, setActive] = useState(active || 1);
    const [metaData, setMetaData] = useState({
        questionType: "",
        isLogicEnable: false,
    });

    const [addLogicModal, setAddLogicModal] = useState({
        open: false,
    });

    const closeDropdown = useCallback(() => {
        setListOpen(false);
        dropdownOpen(false);
    }, [dropdownOpen]);

    const closeDropdownIfOpen = useCallback(() => {
        setListOpen((open) => {
            if (open) {
                dropdownOpen(false);
                return false;
            }
            return open;
        });
    }, [dropdownOpen]);

    useClickOutside(wrapperRef, closeDropdown);

    const changeActiveQuestion = useCallback(
        (index, data) => {
            setActive((prev) => (prev === index ? prev : index));
            const dataAnswer = data.filter(
                (item, indexItem) => indexItem + 1 === index
            )[0];
            if (!dataAnswer) return;
            const nextMeta = {
                questionType: dataAnswer.question_type,
                isLogicEnable: Boolean(
                    isLogic && dataAnswer.questions.is_logic
                ),
            };
            setMetaData((prev) =>
                prev.questionType === nextMeta.questionType &&
                    prev.isLogicEnable === nextMeta.isLogicEnable
                    ? prev
                    : nextMeta
            );
        },
        [isLogic]
    );

    const showPreQuestion = (direction) => {
        if (direction === "increase") {
            if (isActive !== 1) {
                changeActiveQuestion(isActive - 1, questionArray);
                changeActiveQuestionProp(isActive - 1);
            }
        } else if (questionArray.length !== isActive) {
            changeActiveQuestion(isActive + 1, questionArray);
            changeActiveQuestionProp(isActive + 1);
        }
    };

    const questionListSyncKey = questionArray
        .map((q, idx) => `${questionRowKey(q, idx)}:${q?.question_type ?? ""}`)
        .join("|");

    useEffect(() => {
        changeActiveQuestion(active || 1, questionArray);
    }, [active, changeActiveQuestion, questionListSyncKey]);

    useEffect(() => {
        const notify = onHeaderHeightChangeRef.current;
        if (headerRef.current && notify) {
            notify(headerRef.current.offsetHeight);
        }
    }, [isActive]);

    const hidePrototypeNav =
        test.methodology === "Prototype test" &&
        !isActiveDropdown &&
        !questionType;

    const methodologyNotFirstOrTree =
        test.methodology !== "First click" &&
        test.methodology !== "Tree Test";
    const isFirstClickOrTreeIntro =
        (test.methodology === "First click" ||
            test.methodology === "Tree Test") &&
        questionType === "conclusionOrIntroduction";

    const renderNavArrowPair = () => (
        <div className="bd-que-slide-arrow">
            <a
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    showPreQuestion("increase");
                    closeDropdownIfOpen();
                }}
                className={`${isActive === 1 ? "disabled-link" : ""
                    } dropdown-toggle-disabled`}
            >
                <Icon
                    value="back-arrow"
                    size="medium"
                    colorClass="gray-50-svg dropdown-toggle-disabled"
                />
            </a>
            <a
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    showPreQuestion("decrease");
                    closeDropdownIfOpen();
                }}
                className={`${questionArray.length === isActive ? "disabled-link" : ""
                    } dropdown-toggle-disabled`}
            >
                <Icon
                    value="forward-arrow"
                    size="medium"
                    colorClass="gray-50-svg dropdown-toggle-disabled"
                />
            </a>
        </div>
    );

    const containerClass = `question-dropdown-container result-question-dropdown${metaData.isLogicEnable ? " logic-enabled" : ""
        }`;

    return (
        <div className={containerClass} ref={wrapperRef}>
            {test.methodology === "Survey" && isLogic && (
                <>
                    {questionArray.map((items, index) => (
                        <Fragment key={questionRowKey(items, index)}>
                            {isActive === index + 1 &&
                                items.questions.is_logic === 1 && (
                                    <div
                                        className="add-logic-enabled"
                                        onClick={() => {
                                            setAddLogicModal({
                                                open: true,
                                                test_id: test.id,
                                                question_id: items.id,
                                                questionIndex: index,
                                            });
                                        }}
                                    >
                                        <Text
                                            type="caption"
                                            cssClasses="data-right"
                                        >
                                            Logic applied
                                        </Text>
                                    </div>
                                )}
                        </Fragment>
                    ))}
                </>
            )}

            <div
                className="dropdown-header"
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (
                        !event.target.classList.contains(
                            "dropdown-toggle-disabled"
                        ) &&
                        questionArray.length > 1
                    ) {
                        setListOpen(!isListOpen);
                        dropdownOpen(!isListOpen);
                    }
                }}
                ref={headerRef}
            >
                <div className="dropdown-header-left">
                    <div className="dropdown-header-top">
                        <p className="text-small-info text-uppercase">
                            <span className="active-question">{isActive}</span>
                            <span className="text-lowercase">of</span>{" "}
                            {questionArray.length} -{" "}
                            {metaData.questionType ? (
                                questionTypeHeaderLabel(metaData.questionType)
                            ) : (
                                ""
                            )}
                            <span className="questions-dropdown-result-count">
                                ({resultCount}{" "}
                                {resultCount > 1 ? " answers" : " answer"})
                            </span>
                        </p>
                    </div>
                    <div className="dropdown-header-bottom">
                        <div className="dropdown-header-selected-question">
                            <div
                                className={`body-text body-text-1 medium-font ${test.language === "ar" ? "arabic-font" : ""
                                    } left-data`}
                            >
                                {questionArray.map((items, index) => (
                                    <Fragment key={questionRowKey(items, index)}>
                                        {isActive === index + 1 && (
                                            <div className="test-header-with-ans-img">
                                                {items.questions.question}
                                                {items.questions.image_url && (
                                                    <TestResultQuestionImage
                                                        type="button"
                                                        url={
                                                            items.questions
                                                                .image_url
                                                        }
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </Fragment>
                                ))}
                            </div>
                        </div>
                        <div className="dropdown-header-arrow-buttons">
                            {methodologyNotFirstOrTree && (
                                <>
                                    {hidePrototypeNav ? null : renderNavArrowPair()}
                                </>
                            )}
                            {isFirstClickOrTreeIntro && renderNavArrowPair()}
                        </div>
                    </div>
                </div>
                <div
                    className={`dropdown-header-right${questionArray.length > 1 ? "" : " disabled-arrow"
                        }`}
                >
                    {methodologyNotFirstOrTree && (
                        <>
                            {hidePrototypeNav ? null : (
                                <div className="dropdown-header-chevron">
                                    {isListOpen ? (
                                        <Icon
                                            value="chevron-up"
                                            size="medium"
                                        />
                                    ) : (
                                        <Icon
                                            value="chevron"
                                            size="medium"
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div className="dropdown-header-arrow-buttons none-for-desktop mobile-nav-arrows">
                {test.methodology !== "First click" && (
                    <>
                        {hidePrototypeNav ? null : renderNavArrowPair()}
                    </>
                )}
                {test.methodology === "First click" &&
                    questionType === "conclusionOrIntroduction" &&
                    renderNavArrowPair()}
            </div>

            {isListOpen && (
                <div className="dropdown-list-hold-wrap">
                    <div className="dropdown-list-container">
                        {questionArray.map((item, index) => {
                            const isCurrent = isActive === index + 1;
                            return (
                                <span
                                    className="dropdown-list-item"
                                    key={questionRowKey(item, index)}
                                    onClick={() => {
                                        closeDropdown();
                                        changeActiveQuestion(
                                            index + 1,
                                            questionArray
                                        );
                                        changeActiveQuestionProp(index + 1);
                                    }}
                                >
                                    <div
                                        className={
                                            isCurrent
                                                ? "dropdown-active-question dropdown-question-item"
                                                : "dropdown-question-item"
                                        }
                                    >
                                        <Text
                                            type="body-text-1"
                                            fontWeight="medium-font"
                                            cssClasses={
                                                test.language === "ar"
                                                    ? "arabic-font"
                                                    : ""
                                            }
                                        >
                                            {item.questions.question}
                                        </Text>{" "}
                                        {item.questions.questionType && (
                                            <Tag
                                                title={questionTypeTagLabel(
                                                    item.questions.questionType
                                                )}
                                                type={
                                                    isCurrent
                                                        ? "purple-tag"
                                                        : "pink-tag"
                                                }
                                            />
                                        )}
                                    </div>
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}

            <AddLogicTestResultModal
                test_id={addLogicModal.test_id}
                question_id={addLogicModal.question_id}
                question_index={addLogicModal.questionIndex}
                openModal={addLogicModal.open}
                close={() => {
                    setAddLogicModal({ ...addLogicModal, open: false });
                }}
            />
        </div>
    );
}

QuestionsDropdown.propTypes = {
    questionArray: PropTypes.array,
    changeActiveQuestionProp: PropTypes.func,
    dropdownOpen: PropTypes.func,
    resultCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    test: PropTypes.object,
    isActiveDropdown: PropTypes.bool,
    questionType: PropTypes.string,
    active: PropTypes.number,
    isLogic: PropTypes.bool,
    onHeaderHeightChange: PropTypes.func,
};
